import { PropTypes } from 'prop-types';
import React, { useRef, useState } from 'react';

import * as questionPropTypes from 'src/constants/propTypes/question';

import analytics from 'src/shared/analytics';
import AndMoreEllipses from 'src/components/icons/AndMoreEllipses';
import ButtonDiv from 'src/components/buttons/ButtonDiv';
import ButtonLink from 'src/components/buttons/ButtonLink';
import ConfirmationModal from 'src/components/modals/Confirmation';
import classNames from 'src/components/shared/classNames';
import getUserIdFromStore from 'src/shared/getUserIdFromStore';

import DeletionModalAndLinks from '../DeletionModalAndLinks';
import generatePDF from './generatePDF';
import styles from './styles.module.scss';

const cx = classNames.bind(styles);
const ACCEPTED_FILE_TYPES = 'image/*,application/pdf';

function UpdateOptions(props) {
  const {
    deleteStudySet,
    isAiGenerating,
    isPremium,
    isUndesignated,
    isUpdateOptionsContainerOpen,
    onAiFileSelected,
    questions,
    setIsDownloadingPDF,
    setIsUpdateOptionsContainerOpen,
    studySetId,
    title,
  } = props;

  const questionsSize = (questions && questions.size) || 0;
  const fileInputRef = useRef(null);

  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  function handleAndMoreEllipsesClick() {
    setIsUpdateOptionsContainerOpen(current => !current);
  }

  async function downloadPDF() {
    const userId = getUserIdFromStore();

    if (userId) analytics.identify(userId);

    analytics.track('Download as PDF confirmed');

    setIsDownloadingPDF(true);
    await generatePDF(questions, title);
    setIsDownloadingPDF(false);
  }

  function handleAiClick() {
    if (!isPremium || isAiGenerating) return;

    fileInputRef.current.click();
    setIsUpdateOptionsContainerOpen(false);
  }

  function handleFileChange(event) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = ({ target: { result } }) => {
      onAiFileSelected({
        io   : result,
        name : file.name,
        size : file.size,
        type : file.type,
      });
    };

    reader.readAsDataURL(file);
    event.target.value = null;
  }

  function handleDeleteClick() { setIsDeleteModalOpen(true); }
  function closeDeleteModal() { setIsDeleteModalOpen(false); }

  return (
    <>
      <input
        accept={ ACCEPTED_FILE_TYPES }
        data-automated-test="update-options--ai-file-input"
        onChange={ handleFileChange }
        ref={ fileInputRef }
        style={ { display : 'none' } }
        type="file"
      />

      <div className={ styles.Root }>
        <div
          className={ cx({ AndMoreEllipses : true }) }
          data-automated-test="study-set-show--update-options--handle-and-more-ellipsis-icon"
          onClick={ handleAndMoreEllipsesClick }
        >
          <div className={ styles.EllipsesBackground } />
          <AndMoreEllipses />
        </div>

        <If condition={ isUpdateOptionsContainerOpen }>
          <div className={ styles.OptionsContainer }>
            <div className={ styles.Options }>
              <ButtonLink
                extraClassNames={ styles.ButtonLink }
                isSecondary
                noBorder
                text="Add new question"
                to={ `/study-sets/${studySetId}/edit?purpose=add-new-question` }
                withPlusSign
              />

              <ButtonLink
                extraClassNames={ styles.ButtonLink }
                isSecondary
                noBorder
                text="Edit"
                to={ `/study-sets/${studySetId}/edit` }
                withPencil
              />

              <ButtonDiv
                dataAutomatedTest="study-set-show--update-options--add-questions-with-ai"
                extraClassNames={ cx({
                  ButtonLink : true,
                  isDisabled : !isPremium || isAiGenerating,
                }) }
                handleClick={ handleAiClick }
                iconIsOnLeftSide
                isSecondary
                noBorder
                title={ !isPremium ? 'Only available to premium users' : undefined }
                value={ isAiGenerating ? 'Generating...' : 'Add Questions with AI' }
                withBrain
              />

              <ButtonDiv
                dataAutomatedTest="study-set-show--update-options--download-as-flashcards-to-pdf"
                extraClassNames={ styles.ButtonLink }
                handleClick={ () => setIsPDFModalOpen(true) }
                iconIsOnLeftSide
                isSecondary
                noBorder
                value="Download as flashcards to PDF"
                withPDFIcon
              />

              <If condition={ !isUndesignated }>
                <ButtonDiv
                  dataAutomatedTest="study-set-show--update-options--delete-button"
                  extraClassNames={ styles.ButtonLink }
                  handleClick={ handleDeleteClick }
                  iconIsOnLeftSide
                  isSecondary
                  noBorder
                  value="Delete"
                  withTrashCan
                />
              </If>
            </div>
          </div>
        </If>
      </div>

      <If condition={ isPDFModalOpen }>
        <ConfirmationModal
          closeModal={ () => setIsPDFModalOpen(false) }
          isPositiveAction
          submitAction={ downloadPDF }
          submitActionName="Yes"
          title="Download all your questions as a PDF?"
        >
          You&apos;ll be able to save or print them as flashcards. If you print them,
          you&apos;ll just need to be sure to print double-sided.
        </ConfirmationModal>
      </If>

      <DeletionModalAndLinks
        closeInitialModal={ closeDeleteModal }
        isInitialModalOpen={ isDeleteModalOpen }
        deleteStudySet={ deleteStudySet }
        questionsSize={ questionsSize }
      />
    </>
  );
}

UpdateOptions.propTypes = {
  deleteStudySet                  : PropTypes.func.isRequired,
  isAiGenerating                  : PropTypes.bool,
  isPremium                       : PropTypes.bool,
  isUpdateOptionsContainerOpen    : PropTypes.bool.isRequired,
  isUndesignated                  : PropTypes.bool,
  onAiFileSelected                : PropTypes.func,
  setIsDownloadingPDF             : PropTypes.func.isRequired,
  setIsUpdateOptionsContainerOpen : PropTypes.func.isRequired,
  questions                       : questionPropTypes.questions.isRequired,
  studySetId                      : PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  title                           : PropTypes.string.isRequired,
};

export default UpdateOptions;
